import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useDocumentStatuses from '../../../hooks/useDocumentStatuses';

const DocumentTraceability = ({ traceability, traceabilityLoading, isHidden }) => {
    const DISPLAY_HEADERS = false; // Change to false to hide table headers

    const [whereFromCount, setWhereFromCount] = useState(0)
    const [whereUsedCount, setWhereUsedCount] = useState(0)
  
    const [ whereFromCollapsed, setWhereFromCollapsed ] = useState(() => {
        const stored = localStorage.getItem('documentTraceability.whereFromCollapsed');
        return stored !== null ? stored === 'true' : true; // Default: closed
    });
    const [ whereUsedCollapsed, setWhereUsedCollapsed ] = useState(() => {
        const stored = localStorage.getItem('documentTraceability.whereUsedCollapsed');
        return stored !== null ? stored === 'true' : true; // Default: closed
    });
    
    const {
        documentStatuses,
    } = useDocumentStatuses();
    

    useEffect(() => {
      if (traceabilityLoading || !traceability)
        return

      if (traceability.where_from && traceability.where_from.previous && traceability.where_from.previous.length > 0) {
        setWhereFromCount( traceability.where_from.previous.length )
      }
      
      if (traceability.where_used && traceability.where_used.usage) {
        // Recursive function to count all documents in the usage tree
        const countUsageDocuments = (node) => {
          let count = 1; // Count the current document
          
          // Count included documents
          if (node.included_in && node.included_in.length > 0) {
            node.included_in.forEach(includedUsage => {
              count += 1; // Count the included document
              
              // Count forked documents from this included document
              if (includedUsage.forked_documents && includedUsage.forked_documents.length > 0) {
                includedUsage.forked_documents.forEach(forkedNode => {
                  count += countUsageDocuments(forkedNode);
                });
              }
            });
          }
          
          // Count next version
          if (node.next_version) {
            count += countUsageDocuments(node.next_version);
          }
          
          return count;
        };
        
        // Subtract 1 to exclude the current document from the count
        setWhereUsedCount(Math.max(0, countUsageDocuments(traceability.where_used.usage) - 1));
      }
      
    }, [traceability, traceabilityLoading])
  
    const toggleWhereFrom = () => {
        const newValue = !whereFromCollapsed;
        setWhereFromCollapsed(newValue);
        localStorage.setItem('documentTraceability.whereFromCollapsed', newValue.toString());
    };
    
    const toggleWhereUsed = () => {
        const newValue = !whereUsedCollapsed;
        setWhereUsedCollapsed(newValue);
        localStorage.setItem('documentTraceability.whereUsedCollapsed', newValue.toString());
    };


    const renderWhereFrom = () => {
        return (
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <label className="control-label" style={{cursor: 'pointer', userSelect: 'none'}} onClick={toggleWhereFrom}>
                        <i className={`fa fa-chevron-${whereFromCollapsed ? 'right' : 'down'}`} style={{marginRight: '8px', fontSize: '12px'}}></i>
                        Where From ({whereFromCount})
                    </label>

                    {!whereFromCollapsed && (<><br/><small className="form-info-text" >
                      Backward trace - all direct <i>predecessors of this</i> document version (any project or product).
                    </small></>)}

                    {!whereFromCollapsed && (traceabilityLoading ? (
                        <div className="m-t" style={{fontSize: '14px', color: '#676a6c'}}>
                            <i className="fa fa-spinner fa-spin"></i> Loading...
                        </div>
                    ) : traceability && traceability.where_from && traceability.where_from.previous && traceability.where_from.previous.length > 0 ? (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                {DISPLAY_HEADERS && (
                                    <thead>
                                        <tr>
                                            <th>Project Name</th>
                                            <th>Status</th>
                                            <th>Reference & Title</th>
                                            <th>Document Status</th>
                                        </tr>
                                    </thead>
                                )}
                                <tbody>
                                    {(() => {
                                        const rows = [];
                                        const versions = traceability.where_from.previous.slice().reverse();
                                        
                                        let indent = 0;
                                        // Helper function to calculate status text and indent
                                        const getStatusInfo = (version, prevVersion, currentIndent) => {
                                            let statusText = '';
                                            let objType = 'documents'
                                            let newIndent = currentIndent;
                                          
                                            if (!prevVersion) {
                                                statusText = 'first version';
                                            } else {
                                                if (prevVersion.from_included_doc) {
                                                    statusText = 'forked';
                                                    newIndent = currentIndent + 1;
                                                } else if (version.project_id !== prevVersion.project_id) {
                                                    statusText = 'included';
                                                    objType = 'included_documents'
                                                    newIndent = currentIndent + 1;
                                                } else if (version.issue_number > prevVersion.issue_number) {
                                                    statusText = 'upissue';
                                                } else if (version.revision !== prevVersion.revision) {
                                                    statusText = 'uprevision';
                                                }
                                            }
                                            
                                            return { statusText, objType, indent: newIndent };
                                        };
                                        
                                        versions.forEach((version, index) => {
                                            const prevVersion = index === 0 ? null : versions[index - 1];
                                            const prevIndent = index === 0 ? 0 : rows[index - 1].props["data-indent"] || 0;
                                            const { statusText, objType, indent } = getStatusInfo(version, prevVersion, prevIndent);
                                            const statusName = documentStatuses.find(s => s.id === version.document_status_id)?.name || '';
                                            const paddingLeft = indent * 20;
                                            
                                            rows.push(
                                                <tr key={version.id} data-indent={indent}>
                                                    <td>{version.project_name}</td>
                                                    <td>{statusText}</td>
                                                    <td style={{paddingLeft: `${paddingLeft}px`}}>
                                                        <Link to={`/portfolio/${version.project_id}/${objType}/${version.id}`}>
                                                            {version.reference} - {version.title}
                                                        </Link>
                                                    </td>
                                                    <td>{statusName}</td>
                                                </tr>
                                            );
                                        });
                                        
                                        // Add current document as the last row
                                        if (traceability.current) {
                                            const current = traceability.current;
                                            const lastVersion = versions.length > 0 ? versions[versions.length - 1] : null;
                                            const lastIndent = versions.length > 0 ? rows[versions.length - 1].props["data-indent"] || 0 : 0;
                                            const { statusText, objType, indent } = getStatusInfo(current, lastVersion, lastIndent);
                                            const statusName = documentStatuses.find(s => s.id === current.document_status_id)?.name || '';
                                            const paddingLeft = indent * 20;
                                            
                                            rows.push(
                                                <tr key={current.id} data-indent={indent}>
                                                    <td>{current.project_name}</td>
                                                    <td>{statusText}</td>
                                                    <td style={{paddingLeft: `${paddingLeft}px`}}>
                                                        {current.reference} - {current.title}
                                                    </td>
                                                    <td>{statusName}</td>
                                                </tr>
                                            );
                                        }
                                        
                                        return rows;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                {DISPLAY_HEADERS && (
                                    <thead>
                                        <tr>
                                            <th>Project Name</th>
                                            <th>Status</th>
                                            <th>Reference & Title</th>
                                            <th>Document Status</th>
                                        </tr>
                                    </thead>
                                )}
                                <tbody>
                                    {traceability && traceability.current && (
                                        <tr>
                                            <td>{traceability.current.project_name}</td>
                                            <td>first version</td>
                                            <td>
                                                {traceability.current.reference} - {traceability.current.title}
                                            </td>
                                            <td>{documentStatuses.find(s => s.id === traceability.current.document_status_id)?.name || ''}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderWhereUsed = () => {
        return (
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <label className="control-label" style={{cursor: 'pointer', userSelect: 'none'}} onClick={toggleWhereUsed}>
                        <i className={`fa fa-chevron-${whereUsedCollapsed ? 'right' : 'down'}`} style={{marginRight: '8px', fontSize: '12px'}}></i>
                        Where Used ({whereUsedCount})
                    </label>

                    {!whereUsedCollapsed && (<><br/><small className="form-info-text" >
                      Forward trace - all versions <i>derived from this</i> document version (any project or product).
                    </small></>)}

                    {!whereUsedCollapsed && (traceabilityLoading ? (
                        <div className="m-t" style={{fontSize: '14px', color: '#676a6c'}}>
                            <i className="fa fa-spinner fa-spin"></i> Loading...
                        </div>
                    ) : traceability && traceability.where_used && traceability.where_used.usage ? (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                {DISPLAY_HEADERS && (
                                    <thead>
                                        <tr>
                                            <th>Project Name</th>
                                            <th>Status</th>
                                            <th>Reference & Title</th>
                                            <th>Document Status</th>
                                        </tr>
                                    </thead>
                                )}
                                <tbody>
                                    {(() => {
                                        const rows = [];
                                        
                                        // Helper function to calculate status text for where_used
                                        const getWhereUsedStatusInfo = (doc, prevDoc, isIncluded, isForked) => {
                                            let statusText = '';
                                            let objType = 'documents'

                                            if (!prevDoc) {
                                                statusText = 'this version'; // Current document has no status in where_used
                                            } else if (isForked) {
                                                statusText = 'forked';
                                            } else if (isIncluded) {
                                                statusText = 'included';
                                                objType = 'included_documents'
                                            } else if (doc.issue_number > prevDoc.issue_number) {
                                                statusText = 'upissue';
                                            } else if (doc.revision !== prevDoc.revision) {
                                                statusText = 'uprevision';
                                            }
                                            return { statusText, objType };
                                        };
                                        
                                        // Recursive function to traverse the usage tree
                                        const traverseUsageNode = (node, prevDoc, currentIndent, isIncluded = false, isForked = false) => {
                                            const doc = node.document;
                                            const { statusText, objType } = getWhereUsedStatusInfo(doc, prevDoc, isIncluded, isForked);
                                            const statusName = documentStatuses.find(s => s.id === doc.document_status_id)?.name || '';
                                            const paddingLeft = currentIndent * 20;
                                            const isCurrent = !prevDoc; // First one is current
                                            const title = isCurrent ?
                                                          (<span>{doc.reference} - {doc.title}</span>) :
                                                          (<Link to={`/portfolio/${doc.project_id}/${objType}/${doc.id}`}>{doc.reference} - {doc.title}</Link>)
                                            rows.push(
                                                <tr key={`doc-${doc.id}`}>
                                                    <td>{doc.project_name}</td>
                                                    <td>{statusText}</td>
                                                    <td style={{paddingLeft: `${paddingLeft}px`}}>
                                                    {title}</td>
                                                    <td>{statusName}</td>
                                                </tr>
                                            );
                                            
                                            // Process included documents (indent +1)
                                            if (node.included_in && node.included_in.length > 0) {
                                                node.included_in.forEach(includedUsage => {
                                                    const incDoc = includedUsage.included_document;
                                                    const incStatusName = documentStatuses.find(s => s.id === incDoc.document_status_id)?.name || '';
                                                    const { statusText, objType } = getWhereUsedStatusInfo(incDoc, doc, true, false);
                                                    const incIndent = currentIndent + 1;
                                                    const incPaddingLeft = incIndent * 20;
                                                    
                                                    rows.push(
                                                        <tr key={`inc-${incDoc.id}`}>
                                                            <td>{incDoc.project_name}</td>
                                                            <td>{statusText}</td>
                                                            <td style={{paddingLeft: `${incPaddingLeft}px`}}>
                                                                <Link to={`/portfolio/${incDoc.project_id}/${objType}/${incDoc.id}`}>
                                                                    {incDoc.reference} - {incDoc.title}
                                                                </Link>
                                                            </td>
                                                            <td>{incStatusName}</td>
                                                        </tr>
                                                    );
                                                    
                                                    // Process forked documents from this included document (indent +1 from included)
                                                    if (includedUsage.forked_documents && includedUsage.forked_documents.length > 0) {
                                                        includedUsage.forked_documents.forEach(forkedNode => {
                                                            traverseUsageNode(forkedNode, incDoc, incIndent + 1, false, true);
                                                        });
                                                    }
                                                });
                                            }
                                            
                                            // Process next version (same indent level)
                                            if (node.next_version) {
                                                traverseUsageNode(node.next_version, doc, currentIndent, false, false);
                                            }
                                        };
                                        
                                        // Start traversal from the root usage node with indent 0
                                        traverseUsageNode(traceability.where_used.usage, null, 0);
                                        
                                        return rows;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="table-responsive m-t">
                            <table className="table table-striped">
                                {DISPLAY_HEADERS && (
                                    <thead>
                                        <tr>
                                            <th>Project Name</th>
                                            <th>Status</th>
                                            <th>Reference & Title</th>
                                            <th>Document Status</th>
                                        </tr>
                                    </thead>
                                )}
                                <tbody>
                                    {traceability && traceability.current && (
                                        <tr>
                                            <td>{traceability.current.project_name}</td>
                                            <td>this version</td>
                                            <td>
                                                {traceability.current.reference} - {traceability.current.title}
                                            </td>
                                            <td>{documentStatuses.find(s => s.id === traceability.current.document_status_id)?.name || ''}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (isHidden) {
        return (<span/>)
    }
    
    return (
        <>
            <div className="hr-line-dashed"></div>
            <div className="form-group">
                <label className="col-sm-2 control-label"></label>
                <div className="col-sm-8">
                    <h3>Traceability</h3>
                </div>
            </div>
            {renderWhereFrom()}
            {renderWhereUsed()}
            <div className="hr-line-dashed"></div>
        </>
    );
};

export default DocumentTraceability;
